// [all original imports unchanged]
import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { auth, logout, fetchTeamData } from "../services/firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { YearContext } from "../contexts/YearContext";
import { FaHome, FaTable, FaChartBar, FaTrophy, FaUser } from "react-icons/fa";
import LeagueSelector from "./LeagueSelector";
import LeagueSettings from "./LeagueSettings";

function Navbar() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  const { submitDate } = useContext(YearContext);
  const [isLeagueSettingsOpen, setIsLeagueSettingsOpen] = useState(false);
  const [favoriteTeamLogo, setFavoriteTeamLogo] = useState("");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "synthwave");
    localStorage.setItem("theme", "synthwave");

    if (!user) {
      setFavoriteTeamLogo("");
      return;
    }

    // Reuse the cached team data (shared with the dashboard) for the avatar instead of an
    // extra Firestore read, and no longer write a constant theme back on every page load.
    let active = true;
    fetchTeamData(user)
      .then((data) => {
        if (active) setFavoriteTeamLogo(data?.favoriteTeamLogo || "");
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        if (active) setFavoriteTeamLogo("");
      });

    return () => {
      active = false;
    };
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/");
    closeDrawer();
  };

  const closeDrawer = () => {
    document.getElementById("my-drawer-3").checked = false;
  };

  const currentDate = new Date();

  const getNavItems = () => [
    {
      to: "/",
      label: "Home",
      icon: FaHome,
      isActive: location.pathname === "/",
    },
    {
      to: currentDate < submitDate ? "/predictions" : "/compare",
      label: currentDate < submitDate ? "Predictions" : "Compare",
      icon: FaTable,
      isActive:
        location.pathname === "/predictions" ||
        location.pathname === "/compare",
    },
    {
      to: "/previous-standings",
      label: "History",
      icon: FaTrophy,
      isActive: location.pathname === "/previous-standings",
    },
    {
      to: "/scoring",
      label: "Scoring",
      icon: FaChartBar,
      isActive: location.pathname === "/scoring",
    },
  ];

  const NavItem = ({ to, label, icon: Icon, isActive, onClick }) => (
    <li>
      <Link
        to={to}
        onClick={onClick}
        className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 ${
          isActive
            ? "bg-primary/20 text-primary border border-primary/40 shadow-md shadow-primary/10"
            : "hover:bg-base-100/10 hover:text-primary text-base-content"
        }`}
      >
        <Icon className="w-4 h-4" />
        <span>{label}</span>
        {isActive && (
          <div className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse shadow-sm" />
        )}
      </Link>
    </li>
  );

  const UserAvatar = ({ size = "w-10 h-10" }) => (
    <div
      className={`${size} rounded-full overflow-hidden border-2 border-primary/40 shadow-md`}
    >
      {favoriteTeamLogo ? (
        <img src={favoriteTeamLogo} alt="Team" className="object-cover w-full h-full" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-secondary/40 to-primary/40 flex items-center justify-center">
          <FaUser className="w-1/2 h-1/2 text-primary" />
        </div>
      )}
    </div>
  );

  return (
    <div className="drawer z-50">
      <input id="my-drawer-3" type="checkbox" className="drawer-toggle" />

      {/* Main Content */}
      <div className="drawer-content flex flex-col">
        <div className="navbar bg-base-300 shadow-md border-b border-primary/10 text-base-content">
          {/* Mobile Nav Toggle */}
          <div className="flex-none lg:hidden">
            <label htmlFor="my-drawer-3" className="btn btn-square btn-ghost text-primary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </label>
          </div>

          {/* Brand and Horizontal Menu */}
          <div className="flex-1 flex items-center">
            <Link to="/" className="btn btn-ghost normal-case text-xl text-primary">
              PremPicks
            </Link>
            <ul className="menu menu-horizontal hidden lg:flex ml-4">
              {getNavItems().map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </ul>
          </div>

          {/* League Selector and Avatar */}
          <div className="flex-none flex gap-4 items-center">
            {user && (
              <LeagueSelector onOpenLeagueSettings={() => setIsLeagueSettingsOpen(true)} />
            )}

            {user ? (
              <div className="dropdown dropdown-end">
                <label tabIndex={0} className="btn btn-ghost btn-circle avatar">
                  <UserAvatar />
                </label>
                <ul
                  tabIndex={0}
                  className="menu dropdown-content mt-3 p-2 shadow bg-base-200 rounded-box w-48"
                >
                  <li>
                    <button onClick={() => navigate("/profile")}>Account</button>
                  </li>
                  <li>
                    <button onClick={handleLogout}>Logout</button>
                  </li>
                </ul>
              </div>
            ) : (
              <Link to="/" className="btn btn-ghost text-base">
                Login
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Drawer-side (mobile sidebar) */}
      <div className="drawer-side lg:hidden">
        <label htmlFor="my-drawer-3" className="drawer-overlay" />
        <ul className="menu p-4 w-80 bg-base-200 text-base-content min-h-full space-y-2">
          {getNavItems().map((item) => (
            <NavItem key={item.to} {...item} onClick={closeDrawer} />
          ))}

          {user && (
            <>
              <div className="pt-4 flex flex-col items-center">
                <UserAvatar size="w-16 h-16" />
              </div>
              <li>
                <button onClick={() => { navigate("/profile"); closeDrawer(); }}>
                  Account
                </button>
              </li>
              <li>
                <button onClick={handleLogout}>
                  Logout
                </button>
              </li>
            </>
          )}
        </ul>
      </div>

      <LeagueSettings isOpen={isLeagueSettingsOpen} onClose={() => setIsLeagueSettingsOpen(false)} />
    </div>
  );
}

export default Navbar;
